/**
 * E07-F01-T11 — "insufficient data" is NOT "checked and does not fit".
 *
 * Wave-1b reconciliation (docs/research/wave1b-results.md, finding 4): a row whose AI
 * check produced an EMPTY breakdown (SITAEL, Wallbox — confidence 0.7, no verdict on any
 * criterion) was scored 0% and shown as "Below grade cut (0% match)", i.e. exactly like a
 * company we checked and rejected. Both are real candidates: the check simply found
 * nothing to judge by.
 *
 * This module is the ONE rule that tells the two apart. The state is DERIVED on read
 * (no column, no migration — historical rows classify too), from fields every
 * `research_companies` row already has:
 *
 *   - the row carries scoring evidence (any of the six criteria has a pass / partial /
 *     fail verdict in its breakdown) → NOT insufficient, whatever the score is. A real
 *     0% with six FAILs is a genuine "does not fit".
 *   - otherwise, a check that ran and ended with:
 *       site_unreachable  — the AI run failed because the website did not open
 *                           (blocked / timeout / DNS / 4xx / 5xx / robots);
 *       no_site_text      — the website opened but carried no readable text;
 *       no_model_evidence — the run finished (`ai_run_status='scored'` or the pipeline
 *                           `stage='scored'`) but the model returned no verdict at all.
 *   - a row nothing has checked (raw discovery, manual add) or whose run failed for a
 *     model/provider reason is NOT insufficient — that is `not_checked` (see
 *     research-check-state.ts), a different, already-handled state.
 *
 * Consequences the readers enforce: an insufficient row shows score=null (never "0%"),
 * never gets an automatic grade (so never the Grade-C filler), and carries the reason
 * for the Review table and the Evidence drawer. No AI call is involved anywhere here.
 */
import { z } from 'zod';
import { classifyRunFailure, RUN_FAILURE_MESSAGES } from '../runFailure.js';
export const INSUFFICIENT_DATA_REASONS = [
    'site_unreachable',
    'no_site_text',
    'no_model_evidence',
];
export const insufficientDataReasonSchema = z.enum(INSUFFICIENT_DATA_REASONS);
/** Short state label (Review badge / Evidence subheader). */
export const INSUFFICIENT_DATA_LABEL = 'Insufficient data';
/** One sentence per reason: what happened — never "does not fit". */
export const INSUFFICIENT_DATA_MESSAGES = {
    site_unreachable: 'Insufficient data — the company website did not open, so nothing could be checked.',
    no_site_text: 'Insufficient data — the company website opened but has no readable text to check.',
    no_model_evidence: 'Insufficient data — the check found no evidence for any criterion in the website text.',
};
/** Short reason for the Review-table badge line (the full sentence goes in the tooltip). */
export const INSUFFICIENT_DATA_SHORT = {
    site_unreachable: 'website did not open',
    no_site_text: 'no text on the website',
    no_model_evidence: 'no evidence found',
};
/** The wire shape (ReviewRow.insufficientData / EvidenceDrawer.insufficientData). */
export const insufficientDataSchema = z.object({
    reason: insufficientDataReasonSchema,
    /** The sentence to show (INSUFFICIENT_DATA_MESSAGES[reason]). */
    message: z.string(),
    /** Why the site did not open, in plain words (site_unreachable only), else null. */
    detail: z.string().nullable().default(null),
});
/** The six scored criteria (same set as SCORE_CRITERIA / REVIEW_CRITERIA_KEYS). */
const EVIDENCE_KEYS = ['industry', 'subIndustry', 'geo', 'size', 'founded', 'services'];
const SCORED_VERDICTS = new Set(['pass', 'partial', 'fail']);
/**
 * True when the breakdown carries at least one scored verdict (pass / partial / fail)
 * on one of the six criteria. An empty breakdown — or one holding only 'unclear' /
 * unknown keys — is "no evidence". Pure.
 */
export function hasScoringEvidence(breakdown) {
    if (!breakdown || typeof breakdown !== 'object')
        return false;
    const b = breakdown;
    return EVIDENCE_KEYS.some((k) => typeof b[k] === 'string' && SCORED_VERDICTS.has(b[k]));
}
/** Failure codes that mean "the website itself did not open". */
const SITE_ACCESS_CODES = new Set([
    'SITE_BLOCKED',
    'SITE_RATE_LIMITED',
    'SITE_NOT_FOUND',
    'SITE_SERVER_ERROR',
    'SITE_UNREACHABLE',
    'SITE_TIMEOUT',
    'SITE_DISALLOWED_BY_ROBOTS',
]);
/** Full human text of the state: the reason sentence + the site detail when known. Pure. */
export function describeInsufficientData(d) {
    return d.detail ? `${d.message} ${d.detail}` : d.message;
}
function build(reason, detail = null) {
    return { reason, message: INSUFFICIENT_DATA_MESSAGES[reason], detail };
}
/**
 * Derive the insufficient-data state of one row, or null when the row either has
 * evidence to be scored on or was never (successfully) checked. Pure — see the module
 * header for the rule.
 */
export function deriveInsufficientData(row) {
    if (hasScoringEvidence(row.breakdown))
        return null;
    if (row.aiRunStatus === 'failed') {
        const code = classifyRunFailure(row.aiRunError ?? '');
        if (code === 'SITE_NO_TEXT')
            return build('no_site_text');
        if (SITE_ACCESS_CODES.has(code))
            return build('site_unreachable', RUN_FAILURE_MESSAGES[code]);
        return null; // model / provider failure → not checked, not "insufficient"
    }
    if (row.aiRunStatus === 'scored' || row.stage === 'scored')
        return build('no_model_evidence');
    return null;
}
//# sourceMappingURL=research-insufficient-data.js.map